import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshControl, View } from 'react-native';
import { useQuery } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';

import { PEOPLE_TRAINING_OUTSTANDING_STATUSES } from '../../../common/constants';
import { GET_MY_ROSTER_SHIFTS } from '../../../common/graphql/people/roster';
import { useUserContext } from '../../../common/utils/context';
import { addDaysToRosterDate, compareRosterDates, formatShiftRange } from '../../../common/utils/people/roster';
import { getLocalTime } from '../../../common/utils/time/time';
import { formatDate } from '../../../common/utils/utils';
import goToShellRoute from '../../components/navigation/goToShellRoute';
import Text from '../../components/text/Text';
import { Card, EmptyState, PrimaryButton, Screen } from '../../design/components';
import { GET_MOBILE_TRAINING_ASSIGNMENTS } from '../../graphql/training';
import { useRefreshPage } from '../../hooks';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import useTodayDateKey from '../../hooks/useTodayDateKey';
import { getMenuAccess } from '../../utils/menuAccess';
import { resolveWeatherLocation } from '../../utils/weather';
import useMyClockStatus from '../myDay/useMyClockStatus';
import { MY_DAY_MODE } from '../myDay/useMyDayMode';
import { useDriverDay, useDriverShell } from './DriverDayProvider';
import DriverTrainingBanner from './DriverTrainingBanner';
import RunSwitcher from './components/RunSwitcher';
import { DRIVER_ROUTE, RUN_LOAD_ERROR } from './driverRoutes';
import ClockCard from './home/ClockCard';
import NextShiftCard from './home/NextShiftCard';
import TodayRunCard, { TodayRunLoadingCard } from './home/TodayRunCard';
import ToolsGrid from './home/ToolsGrid';
import useHomeToolKeys from './home/useHomeToolKeys';
import WeatherCard from './home/WeatherCard';
import useHomeWeather from './home/useHomeWeather';

const CLOCK_TICK_MS = 30 * 1000;
// How far ahead Home looks for the next rostered shift after today.
const NEXT_SHIFT_WINDOW_DAYS = 14;
// Home tools until the user edits them: drivers and pickers get these (Michael,
// 24 Oct 2026); everyone else keeps the earlier tiles. Approvals defaults on for
// any role with approval permission; the tools' visibility filter enforces access.
const FIELD_DEFAULT_TOOL_KEYS = ['myAccount', 'roster', 'approvals', 'procedures', 'trainings'];
const DEFAULT_TOOL_KEYS = ['roster', 'tickets', 'approvals', 'procedures', 'trainings', 'warehouse', 'employeeDay', 'pickingWork'];

const getGreeting = (hour) => {
	if (hour < 12) {
		return 'Good morning';
	}
	if (hour < 17) {
		return 'Good afternoon';
	}
	return 'Good evening';
};

const resolveBadgeCount = (count) => {
	const normalized = Number(count) || 0;
	return normalized > 0 ? normalized : null;
};

/**
 * Home for everyone on the redesigned app (SPEC decision 19): greeting, date and
 * time, weather, clock status with Shift pass, today's run (or the picker / no
 * run states) and the tools the user can open.
 */
const HomeView = () => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const {
		user,
		tenant,
		isAVC,
		isDriver,
		isPicker,
		canCompleteAssignedMarketSetups,
		canViewClientsLeads,
		loading: isUserLoading,
		refetch: refetchUser,
	} = useUserContext();
	const shell = useDriverShell();
	const day = useDriverDay();
	const {
		shownKeys: shownToolKeys,
		setToolShown,
		saveShownKeys,
	} = useHomeToolKeys(isDriver || isPicker ? FIELD_DEFAULT_TOOL_KEYS : DEFAULT_TOOL_KEYS);
	// The driver day already watches the user's own clock; training shows the
	// trainee's clock there, so Home then reads the admin's own status itself.
	const usesDayClock = day.isEnabled && !day.isTraining;
	const ownClock = useMyClockStatus({ skip: usesDayClock });
	const clockStatus = usesDayClock ? day.clockStatus : ownClock.clockStatus;
	const canUseClockStatus = Boolean(isAVC);
	const refetchOwnClockStatus = ownClock.refetch;
	const timezone = tenant?.timezone;
	const [now, setNow] = useState(() => Date.now());

	useEffect(() => {
		const interval = setInterval(() => setNow(Date.now()), CLOCK_TICK_MS);
		return () => clearInterval(interval);
	}, []);

	const todayDateKey = useTodayDateKey(timezone);
	const { data: rosterData, refetch: refetchRosterShifts } = useQuery(GET_MY_ROSTER_SHIFTS, {
		variables: {
			fromDate: todayDateKey,
			toDate: addDaysToRosterDate(todayDateKey, NEXT_SHIFT_WINDOW_DAYS),
		},
		// Roster is AVC people-gated; errors must not block Home.
		skip: !isAVC,
		errorPolicy: 'all',
	});
	const rosterShifts = rosterData?.getMyRosterShifts || [];
	const rosterShift = rosterShifts.find(shift => shift?.date === todayDateKey) || null;
	// The server returns shifts in date order; a shift later today still belongs to
	// today (the clock card), so the next one is the first on a later day.
	const nextShift = rosterShifts.find(shift => shift?.date && compareRosterDates(shift.date, todayDateKey) > 0) || null;
	// Practical-only list plus the online-training count, so the tile can advertise
	// trainings that are completed in Sentry People on the web.
	const { data: trainingData, refetch: refetchTrainings } = useQuery(GET_MOBILE_TRAINING_ASSIGNMENTS, {
		variables: {
			page: 0,
			perPage: 1,
			sortBy: 'dueAt',
			sortDirection: 1,
			filter: { statuses: PEOPLE_TRAINING_OUTSTANDING_STATUSES },
		},
		skip: !isAVC,
		errorPolicy: 'all',
	});

	const { refresh: refreshDay } = day;
	const refetchHome = useCallback(() => Promise.all([
		refreshDay(),
		...(isAVC ? [refetchRosterShifts(), refetchTrainings()] : []),
		...(isAVC && !usesDayClock ? [refetchOwnClockStatus()] : []),
	]), [isAVC, refetchOwnClockStatus, refetchRosterShifts, refetchTrainings, refreshDay, usesDayClock]);
	const [refreshPage, isRefreshing] = useRefreshPage(refetchHome);

	const weatherLocation = useMemo(() => resolveWeatherLocation({
		warehouse: day.serviceRun?.startLocation,
		tenantName: tenant?.name,
		tenantRegion: tenant?.region,
	}), [day.serviceRun?.startLocation, tenant?.name, tenant?.region]);
	const weather = useHomeWeather(weatherLocation);

	const openRoute = useCallback((routeName, params) => {
		navigation.push(routeName, params);
	}, [navigation]);

	const localNow = getLocalTime(timezone, now);
	const firstName = (user?.firstname || '').trim();
	const greeting = firstName ? `${getGreeting(localNow.hour())}, ${firstName}` : getGreeting(localNow.hour());
	const dateLine = `${localNow.format('dddd D MMMM')} · ${localNow.format('h:mm a')}`;

	const formatClockTime = value => (value ? getLocalTime(timezone, value).format('h:mm a') : null);
	const shiftLabel = rosterShift ? `Shift ${formatShiftRange(rosterShift.startMinutes, rosterShift.endMinutes)}` : null;
	let clock;
	if (clockStatus.isClockedIn) {
		const since = formatClockTime(clockStatus.lastEventCapturedAt);
		clock = { title: 'Clocked in', detail: since ? `Since ${since}` : shiftLabel, tone: 'success' };
	}
	else if (clockStatus.hasClockedOutToday) {
		const at = formatClockTime(clockStatus.lastEventCapturedAt);
		clock = { title: 'Clocked out', detail: at ? `At ${at}` : shiftLabel, tone: 'secondary' };
	}
	else {
		clock = { title: 'Not clocked in', detail: shiftLabel || 'Scan your shift pass at the kiosk', tone: 'warning' };
	}

	const menuAccess = getMenuAccess({
		user,
		isAVC,
		isDriver,
		isPicker,
		canCompleteAssignedMarketSetups,
		ticketScope: shell.ticketScope,
	});
	const trainingBadge = resolveBadgeCount((Number(trainingData?.getTrainingAssignments?.count) || 0) +
		(Number(trainingData?.getTrainingAssignments?.mobileOnlineCount) || 0));
	// Home tools: the side menu's items (same labels, icons and access as
	// NavigationMenu) except My day, which is Home, plus the Home-only tiles. In
	// this order on Home; today's default tiles keep their earlier order.
	const tools = [
		{ key: 'myAccount', label: 'My account', icon: 'user', visible: true, onPress: () => openRoute('MyAccountView') },
		{ key: 'roster', label: 'My roster', icon: 'calendar', visible: menuAccess.canViewRoster, onPress: () => openRoute('MyRosterView') },
		{ key: 'clockPass', label: 'Shift pass', icon: 'grid', visible: menuAccess.canViewClockPass, onPress: () => openRoute('ClockPassView') },
		{ key: 'tickets', label: 'Tickets', icon: 'tag', visible: menuAccess.canViewTicketList, onPress: () => openRoute('TicketListView') },
		{ key: 'approvals', label: 'Approvals', icon: 'check-square', visible: menuAccess.canApproveChecklists, onPress: () => openRoute('ChecklistApprovalsView') },
		{ key: 'procedures', label: 'Procedures', icon: 'file-text', visible: menuAccess.canViewProcedures, onPress: () => openRoute('ProcedureListView') },
		{ key: 'trainings', label: 'Training', icon: 'book-open', visible: menuAccess.canViewTrainings, badge: trainingBadge, onPress: () => openRoute('TrainingListView') },
		{ key: 'leads', label: 'Leads', icon: 'users', visible: Boolean(canViewClientsLeads), onPress: () => openRoute('LeadListView') },
		// Opens the All Runs tab the way the menu and the tab bar do.
		{ key: 'serviceRuns', label: isDriver && shell.mode !== MY_DAY_MODE.PICKER ? 'My runs' : 'All Runs', icon: 'truck', visible: menuAccess.canViewServiceRuns, onPress: () => goToShellRoute(navigation, navigation.getState(), DRIVER_ROUTE.ALL_RUNS) },
		{ key: 'pointsOfSale', label: 'Points of sale', icon: 'shopping-cart', visible: menuAccess.canViewPointsOfSale, onPress: () => openRoute('PointOfSaleListView') },
		{ key: 'setups', label: 'Setups', icon: 'layers', visible: menuAccess.canViewSetups, onPress: () => openRoute('SetupsView') },
		{ key: 'planServiceRun', label: 'Plan service run', icon: 'calendar', visible: menuAccess.canAddServiceRuns, onPress: () => openRoute('ServiceRunCreatorView') },
		{ key: 'warehouse', label: 'Warehouse', icon: 'package', visible: menuAccess.canViewWarehouseMenu, onPress: () => openRoute('WarehouseView') },
		{ key: 'employeeDay', label: 'View an employee\'s day', icon: 'users', visible: shell.canViewEmployeeDays, onPress: shell.openEmployeeSelector },
		{ key: 'pickingWork', label: 'Help with picking', icon: 'shopping-bag', visible: shell.canShowPickingWork, onPress: () => openRoute(DRIVER_ROUTE.PICKING_MY_DAY) },
		{ key: 'settings', label: 'Settings', icon: 'settings', visible: true, onPress: () => openRoute('SettingsView') },
	]
		.filter(tool => tool.visible)
		.map(tool => ({ ...tool, isShown: shownToolKeys.includes(tool.key) }));

	const renderToday = () => {
		if (shell.mode === MY_DAY_MODE.PICKER && !shell.trainingContext) {
			return (
				<Card tone="current" style={styles.todayCard}>
					<Text style={styles.overline}>Today</Text>
					<Text style={styles.todayTitle}>Picking My Day</Text>
					<Text style={styles.todayDetail}>Your pick lists and packing for today.</Text>
					<PrimaryButton label="Open Picking My Day" onPress={() => openRoute(DRIVER_ROUTE.PICKING_MY_DAY)} />
				</Card>
			);
		}
		if (!day.isEnabled) {
			return null;
		}
		if (day.loading) {
			return <TodayRunLoadingCard />;
		}
		if (day.serviceRun) {
			return (
				<TodayRunCard
					day={day}
					isMyDayEnabled={shell.isMyDayEnabled}
					isClockedIn={clockStatus.isClockedIn}
					canUseClockStatus={canUseClockStatus}
					onOpen={openRoute}
				/>
			);
		}
		if (day.loadError) {
			return (
				<EmptyState
					tone="error"
					title="Run not loaded"
					message={RUN_LOAD_ERROR}
					actionLabel="Try again"
					onAction={refreshDay}
				/>
			);
		}
		if (day.isTraining) {
			return (
				<EmptyState
					title="Run unavailable"
					message="This run is no longer available for training. Return to the employee preview and choose another run."
				/>
			);
		}
		// Admins and other staff without a run see only their tools.
		if (shell.mode !== MY_DAY_MODE.DRIVER && !isDriver) {
			return null;
		}

		return (
			<Card style={styles.todayCard}>
				<Text style={styles.todayTitle}>No run today</Text>
				<Text style={styles.todayDetail}>
					{day.upcomingRun
						? `Your next run: ${formatDate(day.upcomingRun.planned, 'DAY_MONTHWORD_YEAR')}${day.upcomingRun.route?.name ? ` · ${day.upcomingRun.route.name}` : ''}`
						: 'No service run was found from today onward. See your manager if you are expecting one.'}
				</Text>
			</Card>
		);
	};

	// The account loads only from the network: after a start without signal Home has
	// nothing to show (no run, tools or tab bar), so offer a retry instead of a blank page.
	if (!user) {
		return (
			<Screen
				showBack={false}
				isLoading={isUserLoading}
				error={isUserLoading ? undefined : 'Check your connection and try again.'}
				onRetry={() => refetchUser?.().catch(() => {})}
			/>
		);
	}

	return (
		<Screen
			showBack={false}
			banner={<DriverTrainingBanner />}
			refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={refreshPage} />}
			contentContainerStyle={styles.content}
		>
			<View style={styles.greeting}>
				<Text style={styles.dateLine}>{dateLine}</Text>
				<Text style={styles.greetingText} accessibilityRole="header">{greeting}</Text>
			</View>
			<WeatherCard weather={weather} placeLabel={weatherLocation?.label} />
			{
				nextShift && (
					<NextShiftCard
						shift={nextShift}
						todayDateKey={todayDateKey}
						onPress={menuAccess.canViewRoster ? () => openRoute('MyRosterView') : undefined}
					/>
				)
			}
			{
				canUseClockStatus && (
					<ClockCard
						title={clock.title}
						detail={clock.detail}
						tone={clock.tone}
						onOpenShiftPass={() => openRoute('ClockPassView')}
					/>
				)
			}
			<RunSwitcher runs={day.todaysRuns} activeRunId={day.serviceRun?.id} onSelectRun={day.setActiveRunId} />
			{renderToday()}
			<ToolsGrid tools={tools} onToggleTool={setToolShown} onDoneEditing={saveShownKeys} />
		</Screen>
	);
};

const createStyles = ({ ui: { colors, type, space, radius } }) => ({
	content: {
		paddingTop: 22,
		gap: space.lg,
	},
	greeting: {
		gap: 6,
	},
	dateLine: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
	greetingText: {
		...type.title,
		color: colors.text,
	},
	todayCard: {
		gap: space.sm,
		borderRadius: radius.hero,
	},
	overline: {
		...type.overline,
		color: colors.accent,
	},
	todayTitle: {
		...type.cardTitle,
		color: colors.text,
	},
	todayDetail: {
		...type.secondary,
		color: colors.textSecondary,
		marginBottom: space.xs,
	},
});

export default HomeView;
